/**
 * DataCompletenessMeter — reusable "modeled vs measured" meter.
 * Shows the 6 value-chain data streams as segments (measured / modeled /
 * empty) with a headline and % progress bar. `compact` renders a tighter
 * variant for the Mission Control home tile.
 */
import React from 'react';
import { CheckCircle2, Sparkles, Circle } from 'lucide-react';

const STATUS_META = {
  measured: { color: 'bg-emerald-500', text: 'text-emerald-700', ring: 'border-emerald-200 bg-emerald-50', Icon: CheckCircle2, label: 'Measured' },
  modeled:  { color: 'bg-cyan-400',    text: 'text-cyan-700',    ring: 'border-cyan-200 bg-cyan-50',       Icon: Sparkles,     label: 'Modeled' },
  empty:    { color: 'bg-slate-200',   text: 'text-slate-400',   ring: 'border-slate-200 bg-slate-50',     Icon: Circle,       label: 'No data' },
};

export default function DataCompletenessMeter({ completeness, compact = false }) {
  if (!completeness) return null;
  const { streams = [], measured_count, total_streams, percent_measured, headline } = completeness;

  return (
    <div data-testid="data-completeness-meter" className={compact ? '' : 'space-y-4'}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className={`font-semibold text-slate-900 ${compact ? 'text-sm' : 'text-base'}`} data-testid="completeness-headline">
            {headline}
          </div>
          {!compact && (
            <div className="text-xs text-slate-500">Replace modeled streams with your own data to strengthen every number.</div>
          )}
        </div>
        <span className={`font-bold ${compact ? 'text-lg' : 'text-2xl'} text-emerald-600`}>{percent_measured}%</span>
      </div>

      {/* segmented progress bar */}
      <div className={`flex gap-1 ${compact ? 'mt-2' : ''}`}>
        {streams.map((s) => {
          const m = STATUS_META[s.status] || STATUS_META.empty;
          return (
            <div key={s.id} className={`h-2 flex-1 rounded-full ${m.color}`}
              title={`${s.label}: ${m.label}`} data-testid={`completeness-seg-${s.id}`} />
          );
        })}
      </div>

      {!compact && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
          {streams.map((s) => {
            const m = STATUS_META[s.status] || STATUS_META.empty;
            const Icon = m.Icon;
            return (
              <div key={s.id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${m.ring}`}
                data-testid={`completeness-stream-${s.id}`}>
                <Icon className={`h-4 w-4 shrink-0 ${m.text}`} />
                <div className="min-w-0">
                  <div className="text-xs font-medium text-slate-700 truncate">{s.label}</div>
                  <div className={`text-[10px] ${m.text}`}>{m.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {compact && (
        <div className="text-[11px] text-slate-500 mt-1.5">
          {measured_count}/{total_streams} measured · the rest are modeled from benchmarks
        </div>
      )}
    </div>
  );
}
